import adminRequest from 'utils/adminRequest'

/**
 * Live progress via Server-Sent Events. Returns an EventSource subscribed to
 * this job's topic; events carry { type: 'PROGRESS'|'COMPLETED'|'CONNECTED', ...counters }.
 */
export const subscribeAssignmentSSE = (jobId) => {
  const baseUrl = (adminRequest.defaults.baseURL || process.env.ADMIN_API_URL || '').replace(/\/$/, '')
  const topic = `assessment-assignment:${jobId}`
  return new EventSource(`${baseUrl}/events/subscribe?topic=${encodeURIComponent(topic)}`)
}

/**
 * Assessment Assignment Activity API.
 *
 * Backend (admin-node) async assignment pipeline. Responses follow the
 * platform convention: payload is at `res.data.data`.
 */

// Server-paginated assignment jobs (all types) for the Activity view.
// Returns { jobs, total }. Pass entityType to scope to the college/corporate tab.
export const getAssignments = (params = {}) =>
  adminRequest.get(`/assessment/assignments`, { params })

// Progress snapshot for one job (counters + state + per-status breakdown).
export const getAssignmentStatus = (jobId) =>
  adminRequest.get(`/assessment/assignment/${jobId}`)

// Server-paginated items in a given status ('failed'|'pending'|'done'|'all').
// Returns { items, total } so the table never loads all 1000+ rows at once.
export const getAssignmentItems = (jobId, { status = 'failed', limit = 25, offset = 0 } = {}) =>
  adminRequest.get(`/assessment/assignment/${jobId}/failures`, { params: { status, limit, offset } })

// Resolve a Manage-Assessments set-group row → its assignment job (+ progress).
export const getAssignmentByGroup = (groupId) =>
  adminRequest.get(`/assessment/assignment/by-group/${groupId}`)

// Jobs for a schedule (lifecycle timeline: diagnosis job + run jobs).
export const getScheduleAssignments = (scheduleId) =>
  adminRequest.get(`/assessment/schedule/${scheduleId}/assignments`)

// Retry failed items. `items` = [{ itemId, studentPayload? }] for edit-and-retry,
// or omit/empty to retry all failed.
export const retryAssignment = (jobId, items) =>
  adminRequest.post(`/assessment/assignment/${jobId}/retry`, items && items.length ? { items } : {})

// ── Calculation (scoring) activity — sibling view to assignment ─────────────
// Counts over the job's assigned students: { total, submitted, scored, pendingCalc, failedCalc, processing }.
export const getCalcStatus = (jobId) =>
  adminRequest.get(`/assessment/assignment/${jobId}/calc`)

// Server-paginated per-student calc rows ('failed'|'pending'|'done'|'all').
export const getCalcItems = (jobId, { status = 'failed', limit = 25, offset = 0 } = {}) =>
  adminRequest.get(`/assessment/assignment/${jobId}/calc/items`, { params: { status, limit, offset } })

// Retry scoring. `ids` = [assessment_assigned_id] for a subset, or omit to retry all failed.
export const retryCalc = (jobId, ids) =>
  adminRequest.post(`/assessment/assignment/${jobId}/calc/retry`, ids && ids.length ? { ids } : {})

/**
 * Poll a job's status until a terminal state (or stopped). Returns a stop fn.
 * Used as the live-progress source until SSE is wired (F2).
 *
 *   const stop = pollAssignmentStatus(jobId, onUpdate, { intervalMs: 1500 })
 *   // later: stop()
 */
export const pollAssignmentStatus = (jobId, onUpdate, { intervalMs = 1500 } = {}) => {
  let stopped = false
  let timer = null
  const TERMINAL = ['completed', 'completed_with_errors', 'failed']

  const tick = async () => {
    if (stopped) return
    try {
      const res = await getAssignmentStatus(jobId)
      const data = res.data?.data
      if (data) {
        onUpdate(data)
        if (TERMINAL.includes(data.state)) { stopped = true; return }
      }
    } catch (err) {
      // transient network error — keep polling
    }
    if (!stopped) timer = setTimeout(tick, intervalMs)
  }
  tick()

  return () => { stopped = true; if (timer) clearTimeout(timer) }
}
